import { Fragment, useEffect, useState, useMemo } from 'react';
import type { FC } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Divider, Drawer, IconButton, List, Typography } from '@material-ui/core';
import { DashboardSidebarItem } from './dashboard-sidebar-item';
import { Scrollbar } from './scrollbar';
import { ChevronLeft as ChevronLeftIcon } from '../icons/chevron-left';
import { ChevronRight as ChevronRightIcon } from '../icons/chevron-right';
import { useAuth } from '../hooks/use-auth';
import { usePermissions } from '../hooks/use-permissions';
import { Permission } from '../types/user';
import { SUPER_ADMIN_TENANT } from '../types/tenant';
import { getAdminItems, getTenantItems, DashboardMenuItem } from '../utils/dashboard-menu-items';
import { getAssetConfig, isClassificationEnabled } from '../config/asset-config';
import { getAllOriginFlows } from '../types/provenance';
import { resolveFlowIcon } from './provenance/flow-icons';

interface DashboardSidebarProps {
  onPin: () => void;
  pinned: boolean;
}

export const DashboardSidebar: FC<DashboardSidebarProps> = (props) => {
  const { onPin, pinned } = props;
  const { pathname } = useLocation();
  const { tenant } = useAuth();
  const { checkPermission, currentPermissions } = usePermissions();
  const isTenantAdmin = checkPermission(Permission.TENANT_ADMIN);
  // Deliberately not checkPermission(ADMIN): that returns true for a tenant admin too, because it
  // short-circuits on TENANT_ADMIN. Global admin is the ADMIN permission itself.
  const isGlobalAdmin = currentPermissions.includes(Permission.ADMIN);
  const [openedItem, setOpenedItem] = useState<DashboardMenuItem | null>(null);
  const [activeItem, setActiveItem] = useState<DashboardMenuItem | null>(null);
  const [activeHref, setActiveHref] = useState('');

  const classificationEnabled = useMemo(
    () => isClassificationEnabled(getAssetConfig(tenant)),
    [tenant],
  );

  const items = useMemo(() => {
    if (!tenant || tenant.id === SUPER_ADMIN_TENANT.id) {
      return getAdminItems();
    }
    const base = getTenantItems()
      // Origin Identity section only makes sense with classification enabled.
      .filter((i) => !i.requiresClassification || classificationEnabled)
      // Admin-only items (e.g. provenance settings) are hidden from non-admins.
      .filter((i) => !i.requiresTenantAdmin || isTenantAdmin)
      .filter((i) => !i.requiresGlobalAdmin || isGlobalAdmin)
      .map((i): DashboardMenuItem => ({ ...i, href: i.href?.replace(':tenantId', tenant.id) }));

    // Origin Identity flows: inject the custom flows after the two built-in items, so they group
    // under the same section header. The built-ins keep their static icons (only custom flows carry
    // a configurable one).
    if (classificationEnabled) {
      const flowItems: DashboardMenuItem[] = getAllOriginFlows(tenant.publicMetadata)
        .filter((f) => !f.locked)
        .map((flow) => ({
          section: 'Origin Identity',
          icon: resolveFlowIcon(flow.icon, flow.operation),
          title: flow.name,
          href: `/tenants/${tenant.id}/origin/flow/${flow.id}`,
        }));
      if (flowItems.length) {
        const lastOrigin = base.map((i) => i.section).lastIndexOf('Origin Identity');
        base.splice(lastOrigin >= 0 ? lastOrigin + 1 : base.length, 0, ...flowItems);
      }
    }
    return base;
  }, [tenant, classificationEnabled, isTenantAdmin, isGlobalAdmin]);

  const handleOpenItem = (item: DashboardMenuItem): void => {
    if (openedItem === item) {
      setOpenedItem(null);
      return;
    }

    setOpenedItem(item);
  };

  useEffect(() => {
    let foundActive = false;

    items.forEach((item) => {
      if (item.items) {
        for (let index = 0; index < item.items.length; index++) {
          const active = matchPath({ path: item.items[index].href, end: true }, pathname);

          if (active) {
            foundActive = true;
            setActiveItem(item);
            setActiveHref(item.items[index].href);
            setOpenedItem(item);
            break;
          }
        }
      } else {
        const active = !!matchPath({ path: item.href, end: true }, pathname);

        if (active) {
          foundActive = true;
          setActiveItem(item);
          setOpenedItem(item);
        }
      }
    });

    // If active item not found, try parents
    if (!foundActive) {
      items.forEach((item) => {
        if (item.href) {
          const active = !!matchPath({ path: item.href, end: false }, pathname);
          if (active) {
            setActiveItem(item);
            setOpenedItem(item);
          }
        }
      });
    }
  }, [pathname, items]);

  return (
    <Drawer
      open
      sx={{ zIndex: 1000 }}
      variant="permanent"
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          height: 'calc(100% - 64px)',
          overflowX: 'hidden',
          top: 64,
          transition: 'width 250ms ease-in-out',
          // Collapsed rail shows icons only; expand explicitly via the pin button
          // (no hover-to-open).
          width: pinned ? 270 : 73,
          '& .simplebar-content': {
            height: '100%',
          },
        },
      }}
    >
      <Scrollbar
        style={{
          display: 'flex',
          flex: 1,
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            p: 2,
          }}
        >
          <List disablePadding>
            {items.map((item, index) => {
              const showSection =
                Boolean(item.section) && item.section !== items[index - 1]?.section;
              return (
                <Fragment key={item.title}>
                  {showSection && (
                    <Typography
                      variant="caption"
                      sx={{
                        // Hidden when the rail is collapsed; the drawer's hover rule reveals
                        // it (alongside the item labels) when expanded.
                        display: pinned ? 'block' : 'none',
                        px: 1.25,
                        mt: index === 0 ? 0 : 2,
                        mb: 0.5,
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {item.section}
                    </Typography>
                  )}
                  <DashboardSidebarItem
                    active={activeItem?.title === item.title}
                    activeHref={activeHref}
                    onOpen={() => handleOpenItem(item)}
                    open={openedItem?.title === item.title && pinned}
                    pinned={pinned}
                    {...item}
                  />
                </Fragment>
              );
            })}
          </List>
          <Box sx={{ flexGrow: 1 }} />
          <Divider />
          <Box sx={{ pt: 1 }}>
            <IconButton onClick={onPin}>
              {pinned ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
        </Box>
      </Scrollbar>
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onPin: PropTypes.func,
  pinned: PropTypes.bool,
};
